import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';

import { DiscoveryResult, NewsResult, Period, ViewRange } from './types';
import { anchorFor, kindColor, visibleMarkers, periodWindow } from './temporal';
import IntervalTracks from './IntervalTracks';
import { percent, periodDates } from './format';

type Props = {
  runId: string;
  result: DiscoveryResult;
  periods?: Period[];
  selected: Period | null;
  onSelect: (period: Period) => void;
  onPreview: (period: Period) => void;
  preview: {
    period: Period;
    news?: NewsResult;
    loading?: boolean;
    error?: string;
  } | null;
  onPreviewEnd: () => void;
  initialRange?: ViewRange | null;
  onRangeChange?: (range: ViewRange) => void;
  disabled?: boolean;
};

const DAY = 86400000;

export const focusRange = (period: Period, start: number, end: number) => {
  const left = new Date(period.start_date).getTime();
  const right = new Date(period.end_date).getTime();
  const padding = Math.max((right - left) * 0.08, 2 * DAY);
  return {
    min: Math.max(start, left - padding),
    max: Math.min(end, right + padding),
  };
};

const PriceChart: React.FC<Props> = ({
  runId,
  result,
  periods,
  selected,
  onSelect,
  onPreview,
  preview,
  onPreviewEnd,
  initialRange,
  onRangeChange,
  disabled,
}) => {
  const chartId = `market-explorer-${runId}`;
  const data = useMemo(() => {
    const points: Array<{ x: number; y: number | null }> = [];
    result.points.forEach((point, index) => {
      const timestamp = new Date(point.date).getTime();
      const previous = result.points[index - 1];
      if (
        previous &&
        (result.coverage_gaps
          ? result.coverage_gaps.some(
              gap =>
                Date.parse(gap.start_date) >= Date.parse(previous.date) &&
                Date.parse(gap.end_date) <= timestamp,
            )
          : timestamp - new Date(previous.date).getTime() > 7 * DAY)
      ) {
        // Nulls break the line without inventing observations across holes.
        points.push({ x: new Date(previous.date).getTime() + DAY, y: null });
        points.push({ x: timestamp - DAY, y: null });
      }
      points.push({ x: timestamp, y: point.close });
    });
    return points;
  }, [result.points, result.coverage_gaps]);
  const first = data[0]?.x || 0;
  const last = data[data.length - 1]?.x || first + DAY;
  const [range, setRange] = useState(initialRange || { min: first, max: last });
  const initializedSelection = useRef(false);
  useEffect(() => {
    onRangeChange?.(range);
  }, [range, onRangeChange]);
  const [showRegimes, setShowRegimes] = useState(true);
  const interacting = useRef(false);
  const pendingRange = useRef<ViewRange | null>(null);
  const syncTimer = useRef<number | null>(null);

  const applyRange = useCallback((next: ViewRange) => {
    if (syncTimer.current !== null) window.clearTimeout(syncTimer.current);
    pendingRange.current = null;
    setRange(current =>
      current.min === next.min && current.max === next.max ? current : next,
    );
  }, []);

  const scheduleRangeSync = useCallback(() => {
    if (syncTimer.current !== null) window.clearTimeout(syncTimer.current);
    if (interacting.current || !pendingRange.current) return;
    syncTimer.current = window.setTimeout(() => {
      if (!interacting.current && pendingRange.current)
        applyRange(pendingRange.current);
    }, 120);
  }, [applyRange]);

  const queueRange = useCallback(
    (next?: ViewRange) => {
      if (!next || !Number.isFinite(next.min) || !Number.isFinite(next.max))
        return;
      // Apex owns the live gesture. Rewriting selection options while dragging
      // resets its origin, making the overview move only a fraction of the drag.
      pendingRange.current = { min: next.min, max: next.max };
      scheduleRangeSync();
    },
    [scheduleRangeSync],
  );

  useEffect(() => {
    const finishInteraction = () => {
      interacting.current = false;
      scheduleRangeSync();
    };
    window.addEventListener('pointerup', finishInteraction);
    window.addEventListener('pointercancel', finishInteraction);
    window.addEventListener('blur', finishInteraction);
    return () => {
      window.removeEventListener('pointerup', finishInteraction);
      window.removeEventListener('pointercancel', finishInteraction);
      window.removeEventListener('blur', finishInteraction);
      if (syncTimer.current !== null) window.clearTimeout(syncTimer.current);
    };
  }, [scheduleRangeSync]);

  useEffect(() => {
    if (!initializedSelection.current) {
      initializedSelection.current = true;
      if (initialRange) return;
    }
    applyRange(
      selected ? focusRange(selected, first, last) : { min: first, max: last },
    );
  }, [selected, first, last, applyRange, initialRange]);

  const options = useMemo<ApexOptions>(() => {
    const visiblePeriods = visibleMarkers(
      periods || result.periods,
      selected,
      range,
    );
    const volatilityRegimes: Array<{
      start_date: string;
      end_date: string;
      volatility: string;
    }> =
      result.regimes ||
      result.periods
        .filter(period => result.regime_ids?.includes(period.id))
        .map(period => ({
          ...period,
          volatility:
            period.volatility || String(period.metrics?.volatility || ''),
        }));
    const selectedBounds = selected ? periodWindow(selected) : null;
    return {
      chart: {
        id: chartId,
        animations: { enabled: false },
        fontFamily: 'inherit',
        foreColor: '#68758a',
        parentHeightOffset: 0,
        zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
        toolbar: {
          show: true,
          autoSelected: 'zoom',
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        events: {
          zoomed: (_chart, details) => {
            queueRange(details?.xaxis);
          },
          beforeResetZoom: () => {
            applyRange({ min: first, max: last });
            return { xaxis: { min: first, max: last } };
          },
          scrolled: (_chart, details) => {
            queueRange(details?.xaxis);
          },
        },
      },
      colors: ['#3768d4'],
      stroke: { curve: 'straight', width: 2 },
      grid: {
        borderColor: '#edf0f5',
        strokeDashArray: 4,
        padding: { top: 12, left: 6, right: 16 },
      },
      dataLabels: { enabled: false },
      markers: { size: 0 },
      tooltip: {
        x: { format: 'dd MMM yyyy' },
        y: {
          formatter: value =>
            value?.toLocaleString('en', { maximumFractionDigits: 5 }) ??
            'No observation',
        },
      },
      xaxis: {
        type: 'datetime',
        min: range.min,
        max: range.max,
        tooltip: { enabled: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: value =>
            value.toLocaleString('en', { maximumFractionDigits: 4 }),
        },
        forceNiceScale: true,
      },
      annotations: {
        xaxis: [
          ...(showRegimes
            ? volatilityRegimes
                .filter(
                  regime =>
                    /high|elevated|stress/i.test(regime.volatility) &&
                    Date.parse(regime.start_date) <= range.max &&
                    Date.parse(regime.end_date) >= range.min,
                )
                .map(regime => ({
                  x: Date.parse(regime.start_date),
                  x2: Date.parse(regime.end_date),
                  fillColor: '#d49b38',
                  opacity: 0.1,
                  borderWidth: 0,
                }))
            : []),
          ...(selected && selectedBounds
            ? [
                {
                  x: selectedBounds.min,
                  x2: selectedBounds.max,
                  fillColor: kindColor(selected.kind),
                  opacity: 0.08,
                  borderWidth: 1,
                  borderColor: kindColor(selected.kind),
                },
                {
                  x: selectedBounds.min,
                  strokeDashArray: 3,
                  borderColor: kindColor(selected.kind),
                  label: {
                    text: 'Period starts',
                    position: 'bottom',
                    orientation: 'horizontal',
                    style: {
                      color: '#fff',
                      background: kindColor(selected.kind),
                      fontSize: '10px',
                    },
                  },
                },
                {
                  x: selectedBounds.max,
                  strokeDashArray: 3,
                  borderColor: kindColor(selected.kind),
                  label: {
                    text: 'Period ends',
                    position: 'bottom',
                    orientation: 'horizontal',
                    style: {
                      color: '#fff',
                      background: kindColor(selected.kind),
                      fontSize: '10px',
                    },
                  },
                },
              ]
            : []),
        ],
        points: visiblePeriods.flatMap(period => {
          const anchor = anchorFor(period);
          const target = Date.parse(anchor.date);
          if (target < range.min || target > range.max) return [];
          // New runs supply the exact original price. Old runs keep the true
          // date, and only use a nearby sample for the vertical coordinate.
          const nearest = data
            .filter(
              item => item.y !== null && Math.abs(item.x - target) <= 3 * DAY,
            )
            .sort((a, b) => Math.abs(a.x - target) - Math.abs(b.x - target))[0];
          const point = {
            x: target,
            y: typeof anchor.price === 'number' ? anchor.price : nearest?.y,
          };
          if (point.y == null) return [];
          const active = period.id === selected?.id;
          const interactions = {
            click: () => !disabled && onSelect(period),
            mouseEnter: () => onPreview(period),
            mouseLeave: onPreviewEnd,
          };
          return [
            {
              id: period.id,
              x: point.x,
              y: point.y,
              ...interactions,
              marker: {
                size: active ? 7 : 5,
                fillColor: active ? kindColor(period.kind) : '#fff',
                strokeColor: kindColor(period.kind),
                strokeWidth: 2,
                cssClass: 'market-explorer__chart-marker',
              },
              label: {
                text: active
                  ? `${anchor.label} · #${result.periods.findIndex(item => item.id === period.id) + 1}`
                  : String(
                      result.periods.findIndex(item => item.id === period.id) +
                        1,
                    ),
                offsetY: -9,
                borderWidth: 0,
                borderRadius: 5,
                ...interactions,
                style: {
                  background: active ? kindColor(period.kind) : '#fff',
                  color: active ? '#fff' : kindColor(period.kind),
                  fontSize: '11px',
                  fontWeight: 700,
                  cssClass: 'market-explorer__chart-marker',
                  padding: { top: 3, bottom: 3, left: 6, right: 6 },
                },
              },
            },
          ];
        }),
      },
    };
  }, [
    chartId,
    data,
    result.periods,
    periods,
    result.regimes,
    result.regime_ids,
    disabled,
    selected,
    range,
    first,
    last,
    showRegimes,
    onSelect,
    onPreview,
    onPreviewEnd,
    queueRange,
    applyRange,
  ]);

  const overview = useMemo<ApexOptions>(
    () => ({
      chart: {
        id: `${chartId}-overview`,
        animations: { enabled: false },
        brush: { enabled: true, target: chartId, autoScaleYaxis: true },
        selection: {
          enabled: true,
          xaxis: range,
          fill: { color: '#3768d4', opacity: 0.1 },
          stroke: { color: '#3768d4', width: 1 },
        },
        toolbar: { show: false },
        parentHeightOffset: 0,
        events: {
          brushScrolled: (_chart, details) => {
            queueRange(details?.xaxis);
          },
        },
      },
      colors: ['#90a8dc'],
      stroke: { width: 1, curve: 'straight' },
      fill: { opacity: 0.12, type: 'solid' },
      dataLabels: { enabled: false },
      markers: { size: 0 },
      tooltip: { enabled: false },
      xaxis: {
        type: 'datetime',
        min: first,
        max: last,
        tooltip: { enabled: false },
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      grid: {
        show: false,
        padding: { top: -12, bottom: -20, left: 12, right: 12 },
      },
    }),
    [chartId, range, first, last, queueRange],
  );

  return (
    <section
      className="market-explorer__chart-panel"
      aria-label={`${result.instrument.ticker} price history`}
      onPointerDownCapture={() => {
        interacting.current = true;
        if (syncTimer.current !== null) window.clearTimeout(syncTimer.current);
      }}
    >
      <div className="market-explorer__chart-heading">
        <div>
          <div className="market-explorer__eyebrow">PRICE HISTORY</div>
          <h2>
            {result.instrument.name}{' '}
            <span>
              {result.instrument.ticker !== result.instrument.name
                ? result.instrument.ticker
                : ''}
            </span>
          </h2>
          <p>
            Visible:{' '}
            {periodDates(
              new Date(range.min).toISOString().slice(0, 10),
              new Date(range.max).toISOString().slice(0, 10),
            )}
            <span className="market-explorer__history-range">
              {' '}
              · History:{' '}
              {periodDates(result.range.start_date, result.range.end_date)}
            </span>
          </p>
        </div>
        <div className="market-explorer__chart-actions">
          <Tooltip title="Amber shading marks periods of elevated historical volatility. These are descriptive price patterns, not economic explanations.">
            <span className="market-explorer__switch-label">
              <Switch
                size="small"
                checked={showRegimes}
                onChange={setShowRegimes}
                aria-label="Show high volatility"
              />{' '}
              High volatility <InfoCircleOutlined />
            </span>
          </Tooltip>
          {selected && (
            <Button
              size="small"
              disabled={disabled}
              onClick={() => applyRange(focusRange(selected, first, last))}
            >
              Focus selected period
            </Button>
          )}
          <Tooltip title="Return to the complete date range. You can also drag the overview below the chart to explore another interval.">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => applyRange({ min: first, max: last })}
            >
              All history
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="market-explorer__chart-canvas">
        <Chart
          type="line"
          height={400}
          options={options}
          series={[{ name: 'Close', data }]}
        />
        {preview && (
          <div className="market-explorer__chart-preview" role="status">
            <strong>{preview.period.title}</strong>
            <span>
              {periodDates(preview.period.start_date, preview.period.end_date)}{' '}
              · {percent(preview.period.return_pct)}
            </span>
            <small>
              {anchorFor(preview.period).label}:{' '}
              {anchorFor(preview.period).date} ·{' '}
              {preview.period.trading_sessions ?? '?'} trading sessions
            </small>
            <p>
              {preview.loading
                ? 'Finding related news…'
                : preview.news?.news?.[0]?.headline ||
                  (preview.error
                    ? 'Select to try loading related news.'
                    : 'Select this period to load its news.')}
            </p>
            <small>Click the marker to explore</small>
          </div>
        )}
      </div>
      <div
        className="market-explorer__overview"
        aria-label="Price history overview: drag to change the visible range"
      >
        <Chart
          type="area"
          height={88}
          options={overview}
          series={[{ name: 'Close', data }]}
        />
      </div>
      <div className="market-explorer__chart-legend">
        <span>
          <i
            className="market-explorer__selected-key"
            style={
              selected
                ? {
                    backgroundColor: `${kindColor(selected.kind)}20`,
                    borderColor: kindColor(selected.kind),
                  }
                : undefined
            }
          />{' '}
          Selected interval
        </span>
        <span>● Named anchor</span>
        {showRegimes && (
          <span>
            <i className="market-explorer__volatility-key" /> High volatility
          </span>
        )}
      </div>
      <IntervalTracks
        periods={periods || result.periods}
        selected={selected}
        range={range}
        onSelect={onSelect}
        disabled={disabled}
      />
      <div className="market-explorer__chart-caption">
        <span>Drag to zoom · use the overview to move through history</span>
        <span>Choose a numbered period below to explore its news</span>
      </div>
    </section>
  );
};

export default PriceChart;
